// src/components/settings/DocumentSettings.js — doced-8f3a61c2 (Document editor, Stage 5d)
//
// Settings › Documents: the company's default customer-facing Terms (organizations/{org}.documentTerms.estimate,
// see utils/documentSettings.js). Owner / Admin / Manager, on companies with the new estimate page.
// Modelled on DepositSettings: a live read of the company record, one Save, a dotted-path update so no other
// company field can be overwritten.
//
// Unsaved changes are obvious (an "Unsaved changes" label, Save only active when there is something to save)
// and leaving asks first, like the estimate page: the browser's own "Leave site?" on close / reload,
// useUnsavedGuard for the app's sidebar and links, and the Settings menu buttons (which switch tabs without
// changing the address) through a click check, as PipelineSettings does.
// If someone else saves different terms while this box has edits, the edits are kept and a note says so.
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/config';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { ScrollText, Info, AlertTriangle, CheckCircle } from 'lucide-react';
import useUnsavedGuard from '../../hooks/useUnsavedGuard';
import {
    ESTIMATE_TERMS_PATH, TERMS_MAX, estimateTermsFromCompany, cleanTerms, termsChanged, termsProblem,
} from '../../utils/documentSettings';

export const LEAVE_MESSAGE = 'You have unsaved changes to your terms. Leave without saving?';

export default function DocumentSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;

    const [saved, setSaved] = useState('');        // what the company record holds now
    const [draft, setDraft] = useState('');        // the box
    const [editBase, setEditBase] = useState('');  // what was saved when this round of editing began
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [error, setError] = useState('');

    const dirty = termsChanged(saved, draft);
    const dirtyRef = useRef(dirty);
    dirtyRef.current = dirty;
    const containerRef = useRef(null);

    useEffect(() => {
        if (!orgId) return undefined;
        return onSnapshot(doc(db, 'organizations', orgId), (snap) => {
            const value = snap.exists() ? estimateTermsFromCompany(snap.data()) : '';
            setSaved(value);
            // Nothing typed yet: follow the saved value. Edits in progress are never overwritten.
            if (!dirtyRef.current) { setDraft(value); setEditBase(value); }
            setLoading(false);
        });
    }, [orgId]);

    // ── leave guard ──────────────────────────────────────────────────────────
    useUnsavedGuard({ when: dirty, message: LEAVE_MESSAGE });
    useEffect(() => {
        if (!dirty) return undefined;
        const onClickCapture = (e) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const el = e.target && e.target.closest ? e.target.closest('[data-settings-tab]') : null;
            if (!el || el.getAttribute('data-settings-tab') === 'documents') return;
            if (containerRef.current && containerRef.current.contains(el)) return;
            if (!window.confirm(LEAVE_MESSAGE)) { e.preventDefault(); e.stopPropagation(); }
        };
        document.addEventListener('click', onClickCapture, true);
        return () => document.removeEventListener('click', onClickCapture, true);
    }, [dirty]);

    const problem = termsProblem(draft);
    const changedElsewhere = dirty && cleanTerms(saved) !== cleanTerms(editBase);

    const handleSave = async () => {
        setError('');
        if (problem) { setError(problem); return; }
        setIsSaving(true);
        try {
            const value = cleanTerms(draft);
            // Dotted path: only documentTerms.estimate changes — never a sibling company field.
            await updateDoc(doc(db, 'organizations', orgId), { [ESTIMATE_TERMS_PATH]: value });
            setSaved(value); setDraft(value); setEditBase(value);
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 3000);
        } catch (err) {
            console.error('Error saving document terms:', err);
            setError(err && err.code === 'permission-denied'
                ? 'Only an Owner, Admin or Manager can change these terms.'
                : 'Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDiscard = () => { setDraft(saved); setEditBase(saved); setError(''); };

    if (loading) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

    const count = cleanTerms(draft).length;

    return (
        <div ref={containerRef} className="p-6 space-y-6 max-w-2xl">
            <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <ScrollText size={18} className="text-gray-500" /> Documents
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    The standard terms your customers see on their documents.
                </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
                <Info size={16} className="shrink-0 mt-0.5 text-blue-600" />
                <p>Changing these terms only affects new estimates. Estimates already created keep the terms they were made with.</p>
            </div>

            {/* Estimate terms */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl space-y-2">
                <div className="flex items-center justify-between gap-3">
                    <label htmlFor="estimate-terms" className="text-sm font-semibold text-gray-800">Estimate terms</label>
                    {dirty && (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">Unsaved changes</span>
                    )}
                </div>
                <p className="text-xs text-gray-500">
                    Printed at the bottom of every estimate PDF and shown on the customer portal. Leave empty if you don't use terms.
                </p>
                <textarea
                    id="estimate-terms"
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); setJustSaved(false); }}
                    rows={10}
                    placeholder="e.g. A 50% deposit is due before work starts. The balance is due on completion."
                    className={`w-full p-3 border rounded-lg text-[15px] leading-relaxed text-gray-900 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 ${problem ? 'border-red-400' : dirty ? 'border-amber-400' : 'border-gray-300'}`}
                />
                <p className={`text-xs text-right ${problem ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                    {count.toLocaleString('en-US')} / {TERMS_MAX.toLocaleString('en-US')} characters
                </p>
            </div>

            {/* Invoice terms — not yet */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2 opacity-70" aria-disabled="true">
                <p className="text-sm font-semibold text-gray-500">Invoice terms</p>
                <p className="text-xs text-gray-500">Coming with the new invoice page.</p>
                <textarea disabled rows={3} className="w-full p-3 border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed resize-none" aria-label="Invoice terms (coming with the new invoice page)" />
            </div>

            {changedElsewhere && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <p>Someone else saved different terms while you were editing. Saving will replace theirs with yours; Discard changes shows theirs.</p>
                </div>
            )}

            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <button
                    onClick={handleSave}
                    disabled={isSaving || !dirty || !!problem}
                    className="min-h-[44px] px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isSaving ? 'Saving…' : 'Save'}
                </button>
                {dirty && !isSaving && (
                    <button
                        type="button"
                        onClick={handleDiscard}
                        className="min-h-[44px] px-4 py-2.5 text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-100"
                    >
                        Discard changes
                    </button>
                )}
                {justSaved && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle size={14} /> Saved
                    </span>
                )}
            </div>
        </div>
    );
}
